import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Download, Loader2 } from 'lucide-react';
import { vehicleApi } from '../services/vehicleApi';
import { dealApi } from '../services/dealApi';
import { companyApi } from '../services/companyApi';
import { cashflowApi } from '../services/cashflowApi';
import { Vehicle, won } from '../types/vehicle';
import { Deal } from '../types/deal';
import { Company } from '../types/company';
import { Cashflow } from '../types/cashflow';
import { VICS_MENU_LABEL, VicsMenu, missingFinancial, missingProfile, missingTerms } from '../lib/vics';
import { VICS_STEPS, VicsStatus, VicsSubmission, vicsApi } from '../services/vicsApi';
import { Tour, TourButton, TourStep } from '../components/Tour';
import { nowLocal } from '../lib/date';
import * as XLSX from 'xlsx';
import { EXIT_METHOD } from '../services/codes';
import { Cadence, currentPeriod, financialYear, fundKind, periodLabel, periodMonths, vicsCadence } from '../lib/compliance';

/**
 * VICS 보고 — 운용 현황을 VICS 에 낸다. 이 화면은 **낼 것을 모으고 빈칸을 찾는다.**
 *
 * 주기는 자금원마다 다르다(2026-10-26, `lib/compliance.ts`).
 *   · 개인투자조합 — 반기. 1313 · 1319 · 1320 → 1329 확정. 1316 재무는 의무가 아니다
 *   · 창업기획자(고유계정) — 매월 1~7일 업무운용상황
 *   · 벤처투자조합 — 월보고로 둔다(주기 확인 필요)
 * 실제 제출과 확정([1329] 보고 검증 및 확정)은 VICS 에서 사람이 한다 — 연계 규격이 공개돼 있지 않다.
 * ⚠️ 확정에 공동인증서가 필요한지는 **확인되지 않았다**(`050-vics-integration.md` 2절) — 단정하지 않는다.
 *
 * 진행 단계는 한 칸씩 — 다음 단계를 누르면 나아가고, 지금 단계를 누르면 한 칸 되돌린다(서버가 때를 적는다).
 *
 * ⚠️ 필수값은 추정이다 (`lib/vics.ts` 머리 주석).
 */
const STEPS = VICS_STEPS;

/**
 * VICS 입력용 엑셀 — 메뉴별 시트(1319 투자유형 · 1320 투자거래(투자 · 회수) · 1313 기업 · 월보고만 1316 재무). VICS 에 옮겨 칠 값을 한 파일로.
 * 빈칸은 비워 둔 채 내보낸다 — 「빈칸」 시트에 무엇이 비었는지 적는다.
 */
function exportVics(
  v: Vehicle, period: string, with1316: boolean,
  trades: { f: Cashflow; d: Deal | undefined; isExit: boolean; shares: number | null; paidOn: string; missing: string[] }[],
  comps: { d: Deal; c: Company | undefined }[],
  fins: any[], year: number,
) {
  const cName = (d?: Deal) => comps.find((x) => x.d.id === d?.id)?.c?.name ?? '';
  const sheet = (rows: (string | number | null | undefined)[][]) => XLSX.utils.aoa_to_sheet(rows.map((r) => r.map((x) => (x == null ? '' : x))));
  const wb = XLSX.utils.book_new();
  // [1319] 투자유형 관리 — 재원 × 투자기업 × 투자유형(증권). 건마다 한 줄이고, 투자계약번호는 VICS 가 만든다.
  const dealRows = trades.filter((t) => !t.isExit && t.d)
    .filter((t, i, a) => a.findIndex((x) => x.d?.id === t.d?.id) === i);
  XLSX.utils.book_append_sheet(wb, sheet([
    ['조합 코드', '투자기업', '투자유형(증권)', '계약일', '최초 납입일', '등기일', '주당 가격', '투자계약번호(VICS 생성)'],
    ...dealRows.map((t) => [v.vicsCode, cName(t.d), t.d?.securityKind, t.d?.contractOn, t.d?.paidOn ?? t.paidOn, t.d?.registeredOn, t.d?.pricePerShare, '']),
  ]), '1319 투자유형');
  // [1320] 투자거래 관리 — 투자와 회수를 **한 시트에** 둔다(VICS 메뉴가 둘을 같이 받는다).
  XLSX.utils.book_append_sheet(wb, sheet([
    ['조합 코드', '투자기업', '구분', '거래일', '주식수', '금액', '회수 방식'],
    ...trades.map((t) => [v.vicsCode, cName(t.d), t.isExit ? '회수' : '투자',
      t.isExit ? t.f.occurredOn : t.paidOn, t.shares, t.f.amount,
      t.isExit ? EXIT_METHOD.toUi(t.d?.exitMethod) : '']),
  ]), '1320 투자거래');
  XLSX.utils.book_append_sheet(wb, sheet([
    ['투자기업', '대표', '사업자번호', '법인번호', '설립일', '본사 주소', '업종코드', '벤처인증', '결산월', '임직원 수', '담당자', '담당자 이메일'],
    ...comps.map(({ c }) => [c?.name, c?.ceo, c?.bizNo, c?.corpNo, c?.foundedOn, c?.address, c?.industryCode, c?.ventureCert == null ? '' : c.ventureCert ? 'Y' : 'N', c?.fiscalMonth, c?.employees, c?.contactName, c?.contactEmail]),
  ]), '1313 기업');
  if (with1316) XLSX.utils.book_append_sheet(wb, sheet([
    ['투자기업', '사업연도', '자산총계', '부채총계', '자본총계', '매출액', '영업이익', '당기순이익'],
    ...comps.map(({ c }) => {
      const f = fins.find((x) => x.companyId === c?.id && x.fiscalYear === year && !x.isForecast);
      return [c?.name, year, f?.totalAssets, f?.totalLiabilities, f?.totalEquity, f?.revenue, f?.operatingIncome, f?.netIncome];
    }),
  ]), '1316 재무');
  const gaps = [
    // 주당 가격은 투자유형(1319)에 적는 값이고, 주식수 · 회수 방식은 거래(1320) 쪽이다
    ...trades.flatMap((t) => t.missing.map((m) => [m.startsWith('주당 가격') ? '1319' : '1320', cName(t.d), m])),
    ...comps.flatMap(({ d, c }) => [
      ...missingProfile(c).map((m) => ['1313', c?.name ?? '', m]),
      ...(with1316 ? missingFinancial(fins.filter((x) => x.companyId === d.companyId), year).map((m) => ['1316', c?.name ?? '', m]) : []),
    ]),
  ];
  XLSX.utils.book_append_sheet(wb, sheet([['메뉴', '투자기업', '빈칸'], ...gaps]), '빈칸');
  XLSX.writeFile(wb, `VICS_${v.vicsCode ?? v.name}_${period}_${nowLocal().replace(' ', '_').replace(':', '')}.xlsx`);
}

const STEP_STYLE: Record<VicsStatus, string> = {
  '작성 중': 'bg-slate-100 text-slate-600',
  '점검 완료': 'bg-indigo-50 text-indigo-600',
  'VICS 입력': 'bg-amber-50 text-amber-700',
  '확정': 'bg-emerald-50 text-emerald-700',
};

const Badge: React.FC<{ menu: VicsMenu }> = ({ menu }) => (
  <span className="rounded px-1.5 py-0.5 text-xs font-semibold bg-sky-50 text-sky-700 tabular-nums" title={VICS_MENU_LABEL[menu]}>
    VICS {menu}
  </span>
);

const Missing: React.FC<{ list: string[] }> = ({ list }) =>
  list.length === 0 ? (
    <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 size={12} /> 완료</span>
  ) : (
    <span className="text-xs text-amber-700">빈칸 {list.length} — {list.join(' · ')}</span>
  );

export const VicsPage: React.FC = () => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [flows, setFlows] = useState<Cashflow[]>([]);
  const [loading, setLoading] = useState(true);
  // 주기마다 따로 고른다 — 월보고는 지난달, 반기 보고는 끝난 반기가 기본
  const [period, setPeriod] = useState<Record<Cadence, string>>({ month: currentPeriod('month'), half: currentPeriod('half') });
  const [subs, setSubs] = useState<VicsSubmission[]>([]);
  const [fins, setFins] = useState<{ companyId: number; fiscalYear: number; isForecast: boolean }[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [tourRun, setTourRun] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, d, c, f, s] = await Promise.all([vehicleApi.list(), dealApi.list(), companyApi.list(), cashflowApi.list(), vicsApi.list()]);
      setVehicles(v); setDeals(d); setCompanies(c); setFlows(f); setSubs(s);
      setFins(await vicsApi.financials(Array.from(new Set(d.map((x) => x.companyId)))));
    } catch (e) { setNotice((e as Error).message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // 대상 — 조합과 창업기획자 고유계정. 자문 · 일임 · 법적 형태 확인 전은 낼 것이 없다
  const isTarget = (v: Vehicle) => v.legalForm === '조합' || v.kind === 'own';
  const targets = vehicles.filter(isTarget);
  const others = vehicles.filter((v) => !isTarget(v));
  const cadenceOf = (vid: number): Cadence => { const v = vehicles.find((x) => x.id === vid); return v ? vicsCadence(v) : 'month'; };
  const groups: { cadence: Cadence; title: string; list: Vehicle[] }[] = [
    { cadence: 'half' as Cadence, title: '반기 보고 · 개인투자조합', list: targets.filter((v) => vicsCadence(v) === 'half') },
    { cadence: 'month' as Cadence, title: '월보고 · 창업기획자 · 벤처투자조합', list: targets.filter((v) => vicsCadence(v) === 'month') },
  ].filter((g) => g.list.length > 0);

  const options = useMemo(() => {
    const out: Record<Cadence, string[]> = { month: [], half: [] };
    for (const c of ['month', 'half'] as Cadence[]) {
      const s = new Set<string>(subs.filter((x) => cadenceOf(x.vehicleId) === c).map((x) => x.month));
      s.add(currentPeriod(c));
      // 대상 자금원의 등록일(없으면 운용 개시일)부터 지금 진행 중인 기간까지 — 지난 보고를 「확정」으로 적고, 다가오는 기간을 미리 준비한다(2026-11-02)
      const starts = vehicles.filter((v) => cadenceOf(v.id) === c).map((v) => v.registeredOn ?? v.startedOn).filter(Boolean) as string[];
      if (starts.length) {
        const first = starts.sort()[0];
        const step = c === 'half' ? 6 : 1;
        const end = new Date(); end.setMonth(end.getMonth() + (c === 'half' ? 6 : 1));
        // 첫 기간 끝 달 — 반기는 06 · 12
        const d = new Date(Number(first.slice(0, 4)), Number(first.slice(5, 7)) - 1, 1);
        if (c === 'half') d.setMonth(d.getMonth() < 6 ? 5 : 11);
        for (let i = 0; i < 400 && d <= end; i++) {
          s.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
          d.setMonth(d.getMonth() + step);
        }
      }
      out[c] = Array.from(s).sort().reverse();
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subs, vehicles]);

  const cName = (id: number) => companies.find((c) => c.id === id)?.name ?? '—';

  /** 다음 칸 → 앞으로, 지금 칸 → 한 칸 뒤로 */
  const step = async (vid: number, key: string, cur: VicsStatus, target: VicsStatus) => {
    const i = STEPS.indexOf(cur), t = STEPS.indexOf(target);
    if (t !== i + 1 && !(t === i && i > 0)) return;
    try {
      const row = t === i + 1 ? await vicsApi.advance(vid, key, cur) : await vicsApi.revert(vid, key, cur);
      setSubs((list) => [...list.filter((s) => s.id !== row.id), row]);
    } catch (e) { setNotice((e as Error).message); }
  };

  const TOUR: TourStep[] = useMemo(() => [
    { name: 'month', target: '[data-tour="month"]', title: '보고 기간', content: '개인투자조합은 반기마다(상반기분 7월, 하반기분 1월), 창업기획자는 매월 1~7일에 냅니다. 보고할 기간을 고릅니다.' },
    { name: 'steps', target: '[data-tour="steps"]', title: '진행 단계', content: '작성 중 → 점검 완료 → VICS 입력 → 확정. 확정은 VICS [1329] 에서 사람이 누릅니다. 여기서는 어디까지 했는지만 표시합니다.', placement: 'bottom' },
    { name: 'trades', target: '[data-tour="trades"]', title: '기간 거래', content: '그 기간 원장의 투자 · 회수 기록입니다. 둘 다 VICS 투자거래(1320)에 들어가고, 투자 건의 증권 · 주당 가격은 투자유형(1319)에 적습니다. 주식수 · 주당 가격 같은 필수값이 비면 표시됩니다.' },
    { name: 'companies', target: '[data-tour="companies"]', title: '보유 기업 정보', content: '보유 기업의 기본정보(1313)입니다. 월보고 대상은 직전 사업연도 재무제표(1316)도 봅니다. 빈칸은 투자처 상세에서 채웁니다.' },
    { name: 'export', target: '[data-tour="export"]', title: 'VICS 입력용 내보내기', content: 'VICS 에 옮겨 칠 값을 한 파일로 받습니다. 자동 전송은 연계 규격을 확인한 뒤에 붙입니다.', placement: 'left' },
  ], []);

  if (loading) {
    return <div className="flex items-center justify-center gap-2 py-24 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> 불러오는 중...</div>;
  }

  const renderVehicle = (v: Vehicle, cadence: Cadence, first: boolean) => {
    const key = period[cadence];
    const months = periodMonths(key, cadence);
    const year = financialYear(key);
    const with1316 = cadence === 'month' && fundKind(v) !== 'ac';
    const s = subs.find((x) => x.vehicleId === v.id && x.month === key);
    const status = s?.status ?? '작성 중';
    const periodFlows = flows.filter((f) => f.vehicleId === v.id && months.includes(f.occurredOn.slice(0, 7))
      && (f.kind === 'investment' || f.kind === 'exit'));
    const myDeals = deals.filter((d) => d.vehicleId === v.id && d.stage !== '검토');
    const trades = periodFlows.map((f) => {
      const d = deals.find((x) => x.id === f.dealId);
      const isExit = f.kind === 'exit';
      // 건의 주식수 · 납입일은 건 전체 값이다 — 이 줄이 그 건의 유일한 투자(또는 전량 회수)일 때만 붙인다.
      // 나눠 들어간 줄은 주식수를 비워 「빈칸」에 올리고, 날짜는 그 줄의 입출금일을 쓴다
      const same = flows.filter((x) => x.dealId !== null && x.dealId === f.dealId && x.kind === f.kind).length;
      const whole = !!d && same === 1 && (!isExit || d.stage === '회수');
      const shares = whole ? d?.shares ?? null : null;
      const paidOn = !isExit && whole ? d?.paidOn ?? f.occurredOn : f.occurredOn;
      return { f, d, isExit, shares, paidOn, missing: missingTerms(d, isExit, !d || whole) };
    });
    const comps = myDeals.map((d) => {
      const c = companies.find((x) => x.id === d.companyId);
      return { d, p1313: missingProfile(c), p1316: with1316 ? missingFinancial(fins.filter((x) => x.companyId === d.companyId), year) : [] };
    });
    const gaps = trades.reduce((n, t) => n + t.missing.length, 0)
      + comps.reduce((n, c) => n + c.p1313.length + c.p1316.length, 0);
    const tour = (name: string) => (first ? { 'data-tour': name } : {});

    return (
      <section key={v.id} className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-slate-200 rounded-lg px-4 py-3 mb-3">
          <div>
            <Link to={`/vehicles/${v.id}`} className="font-semibold text-slate-900 hover:text-indigo-600">{v.name}</Link>
            <div className="text-xs text-slate-500 mt-0.5">
              {v.kind === 'own' ? '창업기획자 업무운용상황' : <>조합 코드 <b className="font-mono font-medium text-slate-700">{v.vicsCode ?? '없음'}</b></>}
              {v.vicsCodeName && <> · {v.vicsCodeName}</>}
            </div>
          </div>
          <div {...tour('steps')} className="flex items-center gap-1">
            {STEPS.map((st, i) => {
              const cur = STEPS.indexOf(status);
              const on = cur >= i;
              const can = i === cur + 1 || (i === cur && i > 0);
              return (
                <button key={st} onClick={() => step(v.id, key, status, st)} disabled={!can}
                  title={i === cur + 1 ? '다음 단계로' : can ? '한 단계 되돌리기' : undefined}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border disabled:cursor-default ${
                    on ? `${STEP_STYLE[st]} border-transparent` : 'bg-white border-slate-200 text-slate-400 hover:bg-slate-50'
                  }`}>
                  {i + 1}. {st}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            {gaps > 0
              ? <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700"><AlertTriangle size={12} /> 빈칸 {gaps}</span>
              : <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700"><CheckCircle2 size={12} /> 빈칸 없음</span>}
            <button {...tour('export')} onClick={() => exportVics(v, key, with1316, trades, comps.map((x) => ({ d: x.d, c: companies.find((y) => y.id === x.d.companyId) })), fins, year)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700">
              <Download size={12} /> VICS 입력용 내보내기
            </button>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <div {...tour('trades')} className="bg-white border border-slate-200 rounded-lg">
            <h2 className="text-sm font-semibold text-slate-900 px-4 py-3 border-b border-slate-100">기간 거래 {trades.length}</h2>
            {trades.length === 0 ? (
              <p className="py-10 text-center text-sm text-slate-500">{periodLabel(key, cadence)} 투자 · 회수 없음</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {trades.map(({ f, d, isExit, missing }) => (
                    <tr key={f.id} className="border-b border-slate-50 last:border-b-0 align-top">
                      <td className="px-4 py-2.5"><Badge menu={isExit ? '1320' : '1319'} /></td>
                      <td className="px-2 py-2.5">
                        <Link to={d ? `/deals/${d.id}` : '#'} className="font-medium text-slate-900 hover:text-indigo-600">{d ? cName(d.companyId) : '—'}</Link>
                        <div className="text-xs text-slate-400 tabular-nums">{f.occurredOn.replace(/-/g, '.')} · {isExit ? '회수' : '투자'} {won(f.amount)}</div>
                        <div className="mt-0.5"><Missing list={missing} /></div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div {...tour('companies')} className="bg-white border border-slate-200 rounded-lg">
            <h2 className="text-sm font-semibold text-slate-900 px-4 py-3 border-b border-slate-100">보유 기업 정보 {comps.length}</h2>
            {comps.length === 0 ? (
              <p className="py-10 text-center text-sm text-slate-500">보유 기업 없음</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {comps.map(({ d, p1313, p1316 }) => (
                    <tr key={d.id} className="border-b border-slate-50 last:border-b-0 align-top">
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <Link to={`/companies/${d.companyId}`} className="font-medium text-slate-900 hover:text-indigo-600">{cName(d.companyId)}</Link>
                      </td>
                      <td className="px-2 py-2.5 space-y-1">
                        <div className="flex items-center gap-2"><Badge menu="1313" /><Missing list={p1313} /></div>
                        {with1316 && <div className="flex items-center gap-2"><Badge menu="1316" /><Missing list={p1316} /></div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </section>
    );
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-1.5">
          VICS 보고 <TourButton onClick={() => setTourRun(true)} />
        </h1>
      </div>

      {notice && (
        <div className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2.5 text-xs text-indigo-800 flex justify-between">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} className="text-indigo-500 hover:text-indigo-800">닫기</button>
        </div>
      )}

      {groups.map((g, gi) => (
        <div key={g.cadence} className="mb-2">
          <div className="flex items-center justify-between gap-3 mb-3">
            <h2 className="text-sm font-semibold text-slate-700">{g.title}</h2>
            <div className="flex items-center gap-2">
              <label htmlFor={`vics-${g.cadence}`} className="text-xs text-slate-500">{g.cadence === 'half' ? '보고 반기' : '보고 월'}</label>
              <select id={`vics-${g.cadence}`} {...(gi === 0 ? { 'data-tour': 'month' } : {})} value={period[g.cadence]}
                onChange={(e) => setPeriod((p) => ({ ...p, [g.cadence]: e.target.value }))}
                className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white">
                {options[g.cadence].map((m) => <option key={m} value={m}>{periodLabel(m, g.cadence)}</option>)}
              </select>
            </div>
          </div>
          {g.list.map((v, i) => renderVehicle(v, g.cadence, gi === 0 && i === 0))}
        </div>
      ))}

      <section className="bg-white border border-slate-200 rounded-lg mb-5">
        <h2 className="text-sm font-semibold text-slate-900 px-4 py-3 border-b border-slate-100">지난 회차</h2>
        {subs.length === 0 && <p className="py-8 text-center text-sm text-slate-500">아직 기록한 회차가 없습니다. 위에서 보고 기간을 고르고 단계를 누르면 여기에 쌓입니다.</p>}
        <table className="w-full text-sm">
          <tbody>
            {[...subs].sort((a, b) => b.month.localeCompare(a.month)).map((s) => (
              <tr key={s.id} className="border-b border-slate-50 last:border-b-0">
                <td className="px-4 py-2.5 tabular-nums text-slate-700">{periodLabel(s.month, cadenceOf(s.vehicleId))}</td>
                <td className="px-2 py-2.5 text-xs text-slate-600">{vehicles.find((v) => v.id === s.vehicleId)?.name}</td>
                <td className="px-2 py-2.5"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STEP_STYLE[s.status]}`}>{s.status}</span></td>
                <td className="px-4 py-2.5 text-right text-xs text-slate-400 tabular-nums">{nowLocal(new Date(s.updatedAt)).slice(0, 10).replace(/-/g, '.')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {others.length > 0 && (
        <p className="text-xs text-slate-400">
          VICS 대상 아님: {others.map((v) => `${v.name}(${v.legalForm ?? '법적 형태 확인 전'})`).join(', ')}
        </p>
      )}

      <Tour steps={TOUR} run={tourRun} onFinish={() => setTourRun(false)} />
    </div>
  );
};
